import React from 'react'
import {
  Card,
  CardContent,
  Typography,
  TextField,
  Button,
  Grid,
  IconButton,
  CircularProgress,
  SvgIcon
} from '@material-ui/core'
import { withStyles } from '@material-ui/core/styles'
import Delete from '@material-ui/icons/Delete'

const styles = theme => ({
  // Primary Tank Styles - greyish with green hint
  primaryCard: {
    minWidth: 340,
    maxWidth: 440,
    borderRadius: 16,
    background: 'linear-gradient(135deg, #f5f5f5 0%, #dfe8e2 100%)',
    boxShadow: '0 2px 12px rgba(0,0,0,0.12)',
    border: '1px solid rgba(0,0,0,0.1)',
    transition: 'all 0.3s ease',
    position: 'relative',
    margin: '12px',
    '&:hover': {
      transform: 'translateY(-2px)',
      boxShadow: '0 4px 16px rgba(0,0,0,0.2)'
    }
  },

  // Secondary Tank Styles - greyish with blue hint
  secondaryCard: {
    minWidth: 340,
    maxWidth: 440,
    borderRadius: 16,
    background: 'linear-gradient(135deg, #f5f5f5 0%, #e2e6f0 100%)',
    boxShadow: '0 2px 12px rgba(0,0,0,0.12)',
    border: '1px solid rgba(0,0,0,0.1)',
    transition: 'all 0.3s ease',
    position: 'relative',
    margin: '12px',
    '&:hover': {
      transform: 'translateY(-2px)',
      boxShadow: '0 4px 16px rgba(0,0,0,0.2)'
    }
  },

  // Numbered Tank Styles (unchanged)
  numberedCard: {
    minWidth: 340,
    maxWidth: 440,
    borderRadius: 16,
    background: 'linear-gradient(135deg, #f5f6f7 0%, #e9ecef 100%)',
    boxShadow: '0 2px 12px rgba(0,0,0,0.05)',
    border: '1px solid rgba(0,0,0,0.08)',
    transition: 'all 0.3s ease',
    position: 'relative',
    margin: '12px',
    '&:hover': {
      transform: 'translateY(-2px)',
      boxShadow: '0 4px 16px rgba(0,0,0,0.08)'
    }
  },

  // Add breathing space inside cards
  cardContent: {
    padding: 24
  },

  gridContainer: {
    spacing: 4
  },

  disabledCard: {
    '&:hover': {
      transform: 'none',
      boxShadow: '0 2px 8px rgba(0,0,0,0.08)'
    }
  },

  // Header Styles
  premiumHeader: {
    borderBottom: '2px solid rgba(255,255,255,0.2)',
    background: 'rgba(255,255,255,0.1)',
    borderRadius: '12px 12px 0 0',
    margin: '-16px -16px 16px -16px',
    padding: '16px 16px 12px 16px',
    backdropFilter: 'blur(10px)',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center'
  },

  standardHeader: {
    borderBottom: '2px solid rgba(0,0,0,0.08)',
    background: 'rgba(255,255,255,0.7)',
    borderRadius: '12px 12px 0 0',
    margin: '-16px -16px 16px -16px',
    padding: '16px 16px 12px 16px',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center'
  },

  headerLeft: {
    display: 'flex',
    alignItems: 'center',
    gap: 12
  },

  // Typography Styles
  primaryTitle: {
    fontWeight: 700,
    fontSize: '1.1rem',
    color: '#2c3e50'
  },

  secondaryTitle: {
    fontWeight: 700,
    fontSize: '1.1rem',
    color: '#2c3e50'
  },

  numberedTitle: {
    fontWeight: 700,
    fontSize: '1.1rem',
    color: '#2c3e50'
  },

  // Chip Styles
  primaryChip: {
    backgroundColor: 'rgba(67,160,71,0.15)',
    color: '#2c3e50',
    fontWeight: 600,
    fontSize: '0.8rem', // ⬆️ larger text
    height: 28, // ⬆️ slightly taller
    borderRadius: 6, // ⬇️ less round, more rectangular
    padding: '0 12px', // ⬆️ more horizontal padding
    display: 'inline-flex',
    alignItems: 'center'
  },

  secondaryChip: {
    backgroundColor: 'rgba(100,149,237,0.15)',
    color: '#2c3e50',
    fontWeight: 600,
    fontSize: '0.8rem',
    height: 28,
    borderRadius: 6,
    padding: '0 12px',
    display: 'inline-flex',
    alignItems: 'center'
  },

  numberedChip: {
    backgroundColor: 'rgba(252, 182, 159, 0.2)',
    color: '#8B4513',
    fontWeight: 600,
    fontSize: '0.8rem',
    height: 28,
    borderRadius: 6,
    padding: '0 12px',
    border: '1px solid rgba(252, 182, 159, 0.4)',
    display: 'inline-flex',
    alignItems: 'center'
  },

  // Button Styles
  deleteButton: {
    backgroundColor: 'rgba(255,255,255,0.1)',
    padding: 8,
    transition: 'all 0.3s ease',
    '&:hover': {
      backgroundColor: 'rgba(231, 76, 60, 0.1)',
      transform: 'scale(1.1)'
    }
  },

  primaryDeleteButton: {
    color: '#2c3e50'
  },

  secondaryDeleteButton: {
    color: '#2c3e50'
  },

  numberedDeleteButton: {
    color: '#e74c3c'
  },

  // TextField Styles
  textFieldRoot: {
    '& .MuiOutlinedInput-root': {
      borderRadius: 12,
      transition: 'all 0.3s ease',
      backgroundColor: 'rgba(255,255,255,0.9)',
      '&:hover .MuiOutlinedInput-notchedOutline': {
        borderWidth: 2
      },
      '&.Mui-focused': {
        backgroundColor: 'rgba(255,255,255,1)',
        '& .MuiOutlinedInput-notchedOutline': {
          borderWidth: 2
        }
      }
    },
    '& .MuiInputLabel-outlined': {
      fontWeight: 500,
      color: 'rgba(0,0,0,0.8)'
    },
    '& .MuiOutlinedInput-input': {
      color: '#2c3e50',
      fontWeight: 500
    }
  },

  primaryTextField: {
    '& .MuiOutlinedInput-root': {
      '&:hover .MuiOutlinedInput-notchedOutline': {
        borderColor: '#43A047'
      },
      '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
        borderColor: '#43A047'
      }
    }
  },

  secondaryTextField: {
    '& .MuiOutlinedInput-root': {
      '&:hover .MuiOutlinedInput-notchedOutline': {
        borderColor: '#1E88E5'
      },
      '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
        borderColor: '#1E88E5'
      }
    }
  },

  numberedTextField: {
    '& .MuiOutlinedInput-root': {
      '&:hover .MuiOutlinedInput-notchedOutline': {
        borderColor: '#fcb69f'
      },
      '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
        borderColor: '#fcb69f'
      }
    }
  },

  // Upload Section Styles
  uploadSection: {
    borderRadius: 16,
    padding: 20,
    border: '2px dashed #e9ecef',
    transition: 'all 0.3s ease'
  },

  premiumUploadSection: {
    background: 'linear-gradient(145deg, #ffffff, #f8f9fa)'
  },

  standardUploadSection: {
    background: 'linear-gradient(145deg, #ffffff, #f8f9fa)'
  },

  uploadTitle: {
    fontWeight: 600,
    marginBottom: 16,
    display: 'flex',
    alignItems: 'center'
  },

  premiumUploadTitle: {
    // color: 'rgba(255,255,255,0.9)'
    color: '#495057'
  },

  standardUploadTitle: {
    color: '#495057'
  },

  uploadButton: {
    borderRadius: 12,
    padding: '12px 24px',
    fontWeight: 600,
    textTransform: 'none',
    fontSize: '0.95rem',
    boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
    color: 'white',
    transition: 'all 0.3s ease',
    '&:hover': {
      transform: 'translateY(-1px)',
      boxShadow: '0 6px 16px rgba(0,0,0,0.2)'
    }
  },

  primaryUploadButton: {
    backgroundColor: '#43A047',
    '&:hover': {
      backgroundColor: '#388E3C'
    }
  },

  secondaryUploadButton: {
    backgroundColor: '#1E88E5',
    '&:hover': {
      backgroundColor: '#1565C0'
    }
  },

  numberedUploadButton: {
    backgroundColor: '#fcb69f',
    '&:hover': {
      backgroundColor: '#f4a688'
    }
  },

  fileStatus: {
    marginTop: 16,
    textAlign: 'center'
  },

  noFileText: {
    fontSize: '0.8rem'
  },

  premiumNoFileText: {
    color: 'rgba(255,255,255,0.7)'
  },

  standardNoFileText: {
    color: '#6c757d'
  },

  successBox: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: 'rgba(40, 167, 69, 0.1)',
    borderRadius: 8,
    padding: '8px 16px'
  },

  inputAdornment: {
    marginRight: 8,
    fontSize: '0.9rem',
    fontWeight: 600
  },

  primaryAdornment: {
    color: '#43A047'
  },

  secondaryAdornment: {
    color: '#1E88E5'
  },

  numberedAdornment: {
    color: '#fcb69f'
  }
})

const TankCard = ({
  classes,
  tankIndex,
  tankData,
  onTankDataChange,
  onRemoveTank,
  onFileUpload,
  isUploading,
  fileName,
  downloadUrl,
  disabled = false,
  title,
  showDeleteButton = true
}) => {
  const handleInputChange = (field, value) => {
    onTankDataChange(tankIndex, field, value)
  }

  const handleFileUpload = event => {
    const file = event.target.files[0]
    if (file) onFileUpload(tankIndex, file)
  }

  // Determine tank type
  const isPrimary = title === 'Primary Tank'
  const isSecondary = title === 'Secondary Tank'
  const isNumbered = !isPrimary && !isSecondary

  // Get appropriate class names
  const getCardClass = () => {
    let cardClass = ''
    if (isPrimary) cardClass = classes.primaryCard
    else if (isSecondary) cardClass = classes.secondaryCard
    else cardClass = classes.numberedCard

    return disabled ? `${cardClass} ${classes.disabledCard}` : cardClass
  }

  const getTextFieldClass = () => {
    let baseClass = classes.textFieldRoot
    if (isPrimary) return `${baseClass} ${classes.primaryTextField}`
    else if (isSecondary) return `${baseClass} ${classes.secondaryTextField}`
    else return `${baseClass} ${classes.numberedTextField}`
  }

  const getTitleClass = () => {
    if (isPrimary) return classes.primaryTitle
    else if (isSecondary) return classes.secondaryTitle
    else return classes.numberedTitle
  }

  const getChipClass = () => {
    if (isPrimary) return classes.primaryChip
    else if (isSecondary) return classes.secondaryChip
    else return classes.numberedChip
  }

  const getDeleteButtonClass = () => {
    let baseClass = classes.deleteButton
    if (isPrimary) return `${baseClass} ${classes.primaryDeleteButton}`
    else if (isSecondary) return `${baseClass} ${classes.secondaryDeleteButton}`
    else return `${baseClass} ${classes.numberedDeleteButton}`
  }

  const getAdornmentClass = () => {
    if (isPrimary)
      return `${classes.inputAdornment} ${classes.primaryAdornment}`
    else if (isSecondary)
      return `${classes.inputAdornment} ${classes.secondaryAdornment}`
    else return `${classes.inputAdornment} ${classes.numberedAdornment}`
  }

  const getUploadButtonClass = () => {
    let baseClass = classes.uploadButton
    if (isPrimary) return `${baseClass} ${classes.primaryUploadButton}`
    else if (isSecondary) return `${baseClass} ${classes.secondaryUploadButton}`
    else return `${baseClass} ${classes.numberedUploadButton}`
  }

  return (
    <Card className={getCardClass()}>
      <CardContent style={{ padding: 16 }}>
        {/* Enhanced Header */}
        <div
          className={
            isPrimary || isSecondary
              ? classes.premiumHeader
              : classes.standardHeader
          }
        >
          <div className={classes.headerLeft}>
            <div className={getChipClass()}>
              {isPrimary
                ? 'PRIMARY TANK'
                : isSecondary
                  ? 'SECONDARY TANK'
                  : `TANK - ${tankData.tankNumber || tankIndex + 1}`}
            </div>
          </div>

          {showDeleteButton && (
            <IconButton
              onClick={() => onRemoveTank(tankIndex)}
              disabled={disabled}
              className={getDeleteButtonClass()}
              style={{ padding: 8 }}
            >
              <Delete fontSize="small" />
            </IconButton>
          )}
        </div>

        <Grid container spacing={3}>
          {/* Min Value */}
          <Grid item xs={12} sm={6}>
            <TextField
              label="Minimum Value"
              variant="outlined"
              value={
                tankData.minValue !== undefined && tankData.minValue !== null
                  ? tankData.minValue
                  : ''
              }
              onChange={e => handleInputChange('minValue', e.target.value)}
              fullWidth
              type="number"
              required
              disabled={disabled}
              className={getTextFieldClass()}
              InputProps={{
                startAdornment: (
                  <MinLevelIcon
                    style={{
                      color: isPrimary
                        ? '#43A047'
                        : isSecondary
                          ? '#1E88E5'
                          : '#fcb69f',
                      marginRight: 8,
                      fontSize: '1.2rem'
                    }}
                  />
                )
              }}
            />
          </Grid>

          {/* Max Value */}
          <Grid item xs={12} sm={6}>
            <TextField
              label="Maximum Value"
              variant="outlined"
              value={
                tankData.maxValue !== undefined && tankData.maxValue !== null
                  ? tankData.maxValue
                  : ''
              }
              onChange={e => handleInputChange('maxValue', e.target.value)}
              fullWidth
              type="number"
              required
              disabled={disabled}
              className={getTextFieldClass()}
              InputProps={{
                startAdornment: (
                  <MaxLevelIcon
                    style={{
                      color: isPrimary
                        ? '#43A047'
                        : isSecondary
                          ? '#1E88E5'
                          : '#fcb69f',
                      marginRight: 8,
                      fontSize: '1.2rem'
                    }}
                  />
                )
              }}
            />
          </Grid>

          {/* Min Tank Capacity */}
          <Grid item xs={12} sm={6}>
            <TextField
              label="Min Tank Capacity"
              variant="outlined"
              value={
                tankData.minCapacity !== undefined &&
                tankData.minCapacity !== null
                  ? tankData.minCapacity
                  : ''
              }
              onChange={e => handleInputChange('minCapacity', e.target.value)}
              fullWidth
              type="number"
              required
              disabled={disabled}
              error={!tankData.isMinCapacityValid}
              helperText={
                !tankData.isMinCapacityValid ? 'Min capacity: 1-999' : ''
              }
              className={getTextFieldClass()}
              InputProps={{
                startAdornment: (
                  <TankIcon
                    style={{
                      color: isPrimary
                        ? '#43A047'
                        : isSecondary
                          ? '#1E88E5'
                          : '#fcb69f',
                      marginRight: 8,
                      fontSize: '1.2rem'
                    }}
                  />
                )
              }}
            />
          </Grid>

          {/* Max Tank Capacity */}
          <Grid item xs={12} sm={6}>
            <TextField
              label="Max Tank Capacity"
              variant="outlined"
              value={
                tankData.maxCapacity !== undefined &&
                tankData.maxCapacity !== null
                  ? tankData.maxCapacity
                  : ''
              }
              onChange={e => handleInputChange('maxCapacity', e.target.value)}
              fullWidth
              type="number"
              required
              disabled={disabled}
              error={!tankData.isMaxCapacityValid}
              helperText={
                !tankData.isMaxCapacityValid ? 'Max capacity: 10-99,999' : ''
              }
              className={getTextFieldClass()}
              InputProps={{
                startAdornment: (
                  <TankMaxIcon
                    style={{
                      color: isPrimary
                        ? '#43A047'
                        : isSecondary
                          ? '#1E88E5'
                          : '#fcb69f',
                      marginRight: 8,
                      fontSize: '1.2rem'
                    }}
                  />
                )
              }}
            />
          </Grid>

          {/* Total Fuel Tank */}
          <Grid item xs={12}>
            <TextField
              label="Total Fuel Tank Capacity"
              variant="outlined"
              value={
                tankData.totalFuelTank !== undefined &&
                tankData.totalFuelTank !== null
                  ? tankData.totalFuelTank
                  : ''
              }
              onChange={e => handleInputChange('totalFuelTank', e.target.value)}
              fullWidth
              type="number"
              required
              disabled={disabled}
              error={!tankData.isTotalFuelTankValid}
              helperText={
                !tankData.isTotalFuelTankValid
                  ? 'Total capacity: 10-99,999'
                  : ''
              }
              className={getTextFieldClass()}
              InputProps={{
                startAdornment: (
                  <FuelIcon
                    style={{
                      color: isPrimary
                        ? '#43A047'
                        : isSecondary
                          ? '#1E88E5'
                          : '#fcb69f',
                      marginRight: 8,
                      fontSize: '1.2rem'
                    }}
                  />
                )
              }}
            />
          </Grid>

          {/* Enhanced File Upload */}
          <Grid item xs={12}>
            <div
              className={`${classes.uploadSection} ${
                isPrimary || isSecondary
                  ? classes.premiumUploadSection
                  : classes.standardUploadSection
              }`}
            >
              <Typography
                variant="subtitle2"
                className={`${classes.uploadTitle} ${
                  isPrimary || isSecondary
                    ? classes.premiumUploadTitle
                    : classes.standardUploadTitle
                }`}
              >
                <DocumentIcon style={{ fontSize: '1.1rem', marginRight: 8 }} />
                Upload Calibration File
              </Typography>

              <input
                accept=".xlsx,.xls"
                style={{ display: 'none' }}
                id={`tank-file-${tankIndex}`}
                type="file"
                onChange={handleFileUpload}
                disabled={disabled}
              />

              <label htmlFor={`tank-file-${tankIndex}`}>
                <Button
                  variant="contained"
                  component="span"
                  disabled={disabled || isUploading}
                  fullWidth
                  className={getUploadButtonClass()}
                >
                  {isUploading ? (
                    <CircularProgress
                      size={20}
                      style={{ marginRight: 12, color: 'white' }}
                    />
                  ) : (
                    <CloudUploadIcon
                      style={{ marginRight: 12, fontSize: '1.2rem' }}
                    />
                  )}
                  {isUploading
                    ? 'Uploading File...'
                    : 'Choose Calibration File'}
                </Button>
              </label>

              <div className={classes.fileStatus}>
                {!fileName ? (
                  <Typography
                    variant="caption"
                    className={`${classes.noFileText} ${
                      isPrimary || isSecondary
                        ? classes.premiumNoFileText
                        : classes.standardNoFileText
                    }`}
                  >
                    Supported formats: XLS, XLSX • Max size: 10MB
                  </Typography>
                ) : (
                  <div className={classes.successBox}>
                    <CheckIcon
                      style={{
                        color: '#28a745',
                        fontSize: '1rem',
                        marginRight: 8
                      }}
                    />
                    <Typography
                      style={{
                        color: '#28a745',
                        fontWeight: 600,
                        marginRight: 8
                      }}
                    >
                      File uploaded successfully
                    </Typography>
                    <Button
                      size="small"
                      href={downloadUrl}
                      target="_blank"
                      style={{
                        color: '#007bff',
                        textTransform: 'none',
                        fontWeight: 600,
                        fontSize: '0.8rem',
                        padding: '2px 8px',
                        minWidth: 'auto'
                      }}
                    >
                      View File
                    </Button>
                  </div>
                )}
              </div>
            </div>
          </Grid>
        </Grid>
      </CardContent>
    </Card>
  )
}

// Custom Icons (compatible with Material-UI v1)
function CloudUploadIcon(props) {
  return (
    <SvgIcon {...props}>
      <path d="M19.35 10.04C18.67 6.59 15.64 4 12 4 9.11 4 6.6 5.64 5.35 8.04 2.34 8.36 0 10.91 0 14 0 17.31 2.69 20 6 20h13c2.76 0 5-2.24 5-5 0-2.64-2.05-4.78-4.65-4.96zM14 13v4h-4v-4H7l5-5 5 5h-3z" />
    </SvgIcon>
  )
}

function TankIcon(props) {
  return (
    <SvgIcon {...props} viewBox="0 0 24 24">
      <path d="M12 2C10.34 2 9 3.34 9 5V6H7C5.34 6 4 7.34 4 9V19C4 20.66 5.34 22 7 22H17C18.66 22 20 20.66 20 19V9C20 7.34 18.66 6 17 6H15V5C15 3.34 13.66 2 12 2ZM12 4C12.55 4 13 4.45 13 5V6H11V5C11 4.45 11.45 4 12 4ZM7 8H17C17.55 8 18 8.45 18 9V19C18 19.55 17.55 20 17 20H7C6.45 20 6 19.55 6 19V9C6 8.45 6.45 8 7 8Z" />
    </SvgIcon>
  )
}

function FuelIcon(props) {
  return (
    <SvgIcon {...props} viewBox="0 0 24 24">
      <path d="M19.77 7.23L19.78 7.22L16.06 3.5C15.67 3.11 15.04 3.11 14.65 3.5C14.26 3.89 14.26 4.52 14.65 4.91L16.73 7H8C6.9 7 6 7.9 6 9V15C6 16.1 6.9 17 8 17H9V19C9 20.1 9.9 21 11 21H13C14.1 21 15 20.1 15 19V17H16C17.1 17 18 16.1 18 15V10.27L19.78 8.49C20.17 8.1 20.17 7.47 19.77 7.23Z" />
    </SvgIcon>
  )
}

function DocumentIcon(props) {
  return (
    <SvgIcon {...props} viewBox="0 0 24 24">
      <path d="M14 2H6C4.9 2 4.01 2.9 4.01 4L4 20C4 21.1 4.89 22 6 22H18C19.1 22 20 21.1 20 20V8L14 2ZM18 20H6V4H13V9H18V20Z" />
    </SvgIcon>
  )
}

function CheckIcon(props) {
  return (
    <SvgIcon {...props} viewBox="0 0 24 24">
      <path d="M9 16.17L4.83 12L3.41 13.41L9 19L21 7L19.59 5.59L9 16.17Z" />
    </SvgIcon>
  )
}

// New icons for MIN/MAX level and Max Tank Capacity
function MinLevelIcon(props) {
  return (
    <SvgIcon {...props} viewBox="0 0 24 24">
      <path d="M12 20l-4-4h8l-4 4zM4 4h16v2H4z" />
    </SvgIcon>
  )
}

function MaxLevelIcon(props) {
  return (
    <SvgIcon {...props} viewBox="0 0 24 24">
      <path d="M12 4l4 4H8l4-4zM4 18h16v2H4z" />
    </SvgIcon>
  )
}

function TankMaxIcon(props) {
  return (
    <SvgIcon {...props} viewBox="0 0 24 24">
      {/* Tank outline similar to TankIcon */}
      <path d="M12 2C10.34 2 9 3.34 9 5V6H7C5.34 6 4 7.34 4 9V19C4 20.66 5.34 22 7 22H17C18.66 22 20 20.66 20 19V9C20 7.34 18.66 6 17 6H15V5C15 3.34 13.66 2 12 2Z" />
      {/* Full(er) level indicator */}
      <path d="M6 15H18V19C18 19.55 17.55 20 17 20H7C6.45 20 6 19.55 6 19V15Z" />
    </SvgIcon>
  )
}

export default withStyles(styles)(TankCard)



// WEBPACK FOOTER //
// ./src/components/Pages/FuelCalibration/TankCard.jsx